import React, { memo } from "react";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { MoneyFigure } from "components/ui";
import { ff } from "theme";
import { formatDeliverableCounts } from "utils/deliverableUtils";
import { offerCardSide, offerCardTitle, offerTermsLine } from "../preCollab";
import type { CreatorOfferAvailability, OfferCard as OfferCardData, OfferCardStatus } from "../preCollab";

const STATUS_LABELS: Record<OfferCardStatus, string> = {
  open: "Open",
  replaced: "Replaced",
  accepted: "Accepted",
  declined: "Declined",
  expired: "Expired",
  canceled: "Canceled",
};

/** How a closed-off campaign changes the respond card. `accept: null` leaves only Decline. */
const AVAILABILITY_DISPLAY: Record<Exclude<CreatorOfferAvailability, "open">, { label: string; accept: string | null; note?: string }> = {
  full: { label: "Campaign full", accept: "Join waitlist" },
  fullEnded: { label: "Full & ended", accept: "Request a spot" },
  ended: { label: "Campaign ended", accept: "Request more time" },
  waitlistRequested: { label: "On waitlist", accept: null, note: "Brand notified — you'll get a text if a spot opens." },
  extensionRequested: { label: "Extension requested", accept: null },
  spotOpened: { label: "Spot opened", accept: "Accept", note: "A spot opened for you — accept to lock it in." },
  checking: { label: "Checking availability…", accept: null },
  slotsBooked: { label: "All slots booked", accept: "Request extension", note: "Every time slot is taken. Ask the brand to add more." },
};

/** The campaign-side reason shown next to "Awaiting brand". */
const AWAITING_SUFFIX: Partial<Record<CreatorOfferAvailability, string>> = {
  full: " · Campaign full",
  fullEnded: " · Campaign full",
  waitlistRequested: " · Campaign full",
  ended: " · Campaign ended",
  extensionRequested: " · Extension requested",
  slotsBooked: " · Slots booked",
};

export interface OfferCardActions {
  onAccept: () => void;
  /** Absent when the campaign allows no (more) counters. */
  onCounter?: () => void;
  onDecline: () => void;
}

interface OfferCardProps {
  card: OfferCardData;
  brandName?: string;
  /** Accept / Counter / Decline, shown only when the card is the creator's turn to respond. */
  actions?: OfferCardActions;
  /** Whether the campaign can still take the offer; decided by the page. Absent = open. */
  availability?: CreatorOfferAvailability;
}

const secondaryButton = {
  variant: "inkOutline",
  h: "44px",
  fontSize: "14px",
  borderRadius: "full",
  flex: "1 1 0",
  minW: "72px",
} as const;

/** A counter/proposal in the thread, rendered from the collab — not a stored message. */
function OfferCard({ card, brandName, actions, availability = "open" }: OfferCardProps) {
  const title = offerCardTitle(card, brandName);
  const display = card.turn === "respond" && availability !== "open" ? AVAILABILITY_DISPLAY[availability] : undefined;
  const acceptLabel = display ? display.accept : "Accept";
  const statusLabel =
    card.turn === "awaitingBrand"
      ? `Awaiting brand${AWAITING_SUFFIX[availability] ?? ""}`
      : display
        ? display.label
        : card.status === "declined" && offerCardSide(card.kind) === "creator"
          ? "Declined by brand"
          : STATUS_LABELS[card.status];
  const terms = offerTermsLine(card);
  const deliverables =
    card.deliverableCounts && Object.keys(card.deliverableCounts).length > 0
      ? formatDeliverableCounts(card.deliverableCounts)
      : "";
  const showActions = !!actions && card.turn === "respond";
  const fromCreator = offerCardSide(card.kind) === "creator";

  return (
    <Flex justify={fromCreator ? "flex-end" : "flex-start"} my="12px" px="8px" data-side={fromCreator ? "creator" : "brand"}>
      <Box
        as="article"
        aria-label={`${title}, ${statusLabel}`}
        w="100%"
        maxW={{ base: "85%", md: "360px" }}
        border="1px solid"
        borderColor={ff.border}
        borderRadius="20px"
        bg={fromCreator ? ff.grey : "white"}
        px="16px"
        py="14px"
        fontFamily="display"
      >
        <Flex justify="space-between" align="center" gap="8px">
          <Text textStyle="eyebrow" color={ff.text2}>{title}</Text>
          <Text
            fontSize="11px"
            fontWeight={600}
            color={card.status === "open" ? ff.ink : card.status === "declined" ? ff.danger : ff.muted}
            flexShrink={0}
          >
            {statusLabel}
          </Text>
        </Flex>
        {terms && (
          <Flex mt="6px" gap="10px" wrap="wrap" align="baseline">
            {(card.cash ?? 0) > 0 && <MoneyFigure value={`$${card.cash}`} label="Cash" />}
            {(card.cash ?? 0) > 0 && (card.credit ?? 0) > 0 && (
              <Text as="span" fontSize="18px" fontWeight={300} color={ff.muted} aria-hidden>+</Text>
            )}
            {(card.credit ?? 0) > 0 && <MoneyFigure value={`$${card.credit}`} label="Credit" />}
          </Flex>
        )}
        {deliverables && <Text fontSize="13px" fontWeight={600} color={ff.ink} mt="4px">{deliverables}</Text>}
        {card.note && (
          <Text fontSize="13px" color={ff.text2} mt="6px" fontStyle="italic" whiteSpace="pre-wrap">
            “{card.note}”
          </Text>
        )}
        {display?.note && <Text fontSize="13px" color={ff.text2} mt="6px">{display.note}</Text>}
        {showActions && (
          <Flex mt="12px" gap="8px" wrap="wrap">
            {acceptLabel && (
              <Button
                variant="ink"
                h="44px"
                fontSize="14px"
                borderRadius="full"
                flex="1 0 100%"
                flexShrink={0}
                onClick={actions.onAccept}
                aria-label={`${acceptLabel} ${title.toLowerCase()}`}
              >
                {acceptLabel}
              </Button>
            )}
            {actions.onCounter && !display && (
              <Button {...secondaryButton} onClick={actions.onCounter} aria-label={`Counter ${title.toLowerCase()}`}>
                Counter
              </Button>
            )}
            <Button {...secondaryButton} onClick={actions.onDecline} aria-label={`Decline ${title.toLowerCase()}`}>
              Decline
            </Button>
          </Flex>
        )}
      </Box>
    </Flex>
  );
}

export default memo(OfferCard);
